import React from 'react';
import '@/v2/pages/Plans/components/pricing-card/PricingCard.css';
import ChargeBeeButton from '@/v2/pages/Plans/components/chargebee-button';
import useActivePlan from '@/v2/pages/Plans/hooks/useActivePlan';
import GlowBorderWrapper from '../glow-border/GlowBorderWrapper';
import { useAtomValue } from 'jotai';
import { userInfoAtom } from '@/data/atoms/utilsAtoms';
import { isMatchingCurrentPlan } from '@/data/utils/subscriptionPlanMatch';
import useIsUSD from '@/hooks/useIsUSD';

const PricingCard = ({
	plan,
	isBestValue,
	isYearly = false,
	isMobile = false,
	onOpenCheckout,
	currentSubscriptionDetails,
	currentSubscriptionLoading = false
}) => {
	const { activePlan, selectedSeat, setSelectedSeat } = useActivePlan(plan);
	const userData = useAtomValue(userInfoAtom);

	const isCurrentPlan = () => {
		const hasActiveSubscription = Boolean(userData.subscriptionId);
		if (!hasActiveSubscription) return false;
		return isMatchingCurrentPlan({ currentSubscriptionDetails, plan, activePlan });
	};

	const { isUSD } = useIsUSD();

	const CardContent = (
		<article
			className={`pricing-card ${isBestValue ? 'pricing-card--best-value' : ''} ${isCurrentPlan() ? 'pricing-card--disabled' : ''}`}
			style={isCurrentPlan() ? { pointerEvents: 'none', opacity: 0.7 } : {}}
		>
			{isBestValue && <div className="pricing-card__popular-badge">most popular</div>}

			<div className="pricing-card__inner">
				<div className="pricing-card__header">
					<div className="pricing-card__title-row">
						<h3 className={isBestValue ? 'icon-large' : 'icon-regular'}>
							<plan.icon />
						</h3>
						<h3 className={isBestValue ? 'title-large' : 'title-regular'}>{plan.title}</h3>
					</div>
					{plan.description && <p className="pricing-card__description">{plan.description}</p>}

					{plan.hasSeatSelection && (
						<div className="pricing-card__seat-row">
							<span>Select:</span>
							{plan.seats.map(seat => {
								const checked = selectedSeat.seatId === seat.seatId;
								return (
									<label key={seat.seatId}>
										<input
											type="radio"
											name={plan.id}
											value={seat.seatId}
											checked={checked}
											onChange={() => setSelectedSeat(seat)}
										/>
										{seat.seatLabel}
									</label>
								);
							})}
						</div>
					)}

					<p className="price">
						${isYearly ? (activePlan.price / 12).toFixed(2) : activePlan.price}{' '}
						{!isUSD && <span className="usd">USD </span>}
						<span className="billing-cycle">per month</span>
					</p>
					{isYearly && <p className="billing-cycle">${activePlan.price.toFixed(2)}{!isUSD && ' USD'} billed yearly</p>}
				</div>

				<div className="button-container">
					<ChargeBeeButton
						isBestValue={isBestValue}
						planId={plan.id}
						planCbItem={activePlan.cbItem}
						hasSeatSelection={plan.hasSeatSelection}
						onOpenCheckout={onOpenCheckout}
						activePlan={activePlan}
						currentSubscriptionDetails={currentSubscriptionDetails}
						currentSubscriptionLoading={currentSubscriptionLoading}
					/>
				</div>

				<div className={`perks ${isBestValue ? 'large' : 'small'}`}>
					<h4 className={`pricing-card__perks-title ${isBestValue ? 'fs-4' : ''}`}>{activePlan.perks.title}:</h4>
					<ul>
						{activePlan.perks.features.map((feature, index) => (
							<li key={index} className={isBestValue ? 'fs-5' : ''}>
								{feature}
							</li>
						))}
					</ul>
				</div>
			</div>
		</article>
	);

	return (
		<div className={`pricing-card__outer ${isBestValue ? 'is-most-popular' : ''}`}>
			{isBestValue && !isMobile ? <GlowBorderWrapper>{CardContent}</GlowBorderWrapper> : CardContent}
		</div>
	);
};

export default PricingCard;
